import {
  ApiResponse,
  Chat,
  Exercise,
  GroupChat,
  Message,
  Plan,
  PlanMeta,
  PlanPhase,
  PlanTemplate,
  PlanTemplateWorkout,
  PlanWeek,
  PlanWorkout,
} from "@readyfit-common/models";
import { createSlice } from "@reduxjs/toolkit";
import { WorkoutFormValues } from "src/grading/components/EditWorkoutModal/EditWorkoutModal";
import { RootState } from "src/store/rootStore";
import { apiSlice } from "../../apiSlice";

type TrainingSliceState = {
  workoutModalOpen: boolean;
  deleteWorkoutModalOpen: boolean;
  selectedWorkout: PlanWorkout | null;
  selectedWeek: PlanWeek | null;
  deleteWeekModalOpen: boolean;
  selectedDate: string | null;
  selectedDayNumber: number | null;
};

type AthleteTrainingPlanResponse = {
  data: {
    plan: Plan | null;
    meta: PlanMeta | null;
  };
};

type SortAthleteTrainingPlanRequest = {
  planId: string;
  phases: {
    id: string;
    weeks: {
      id: string;
    }[];
  }[];
};

type AddAthleteWorkoutRequest = {
  planId: string;
  weekId: string;
  workout: Partial<WorkoutFormValues>;
};

type UpdateAthleteWorkoutRequest = {
  planId: string;
  weekId: string;
  workoutId: string;
  workout: WorkoutFormValues;
};

type DeleteAthleteWorkoutRequest = {
  planId: string;
  weekId: string;
  workoutId: string;
};
type DeleteAthleteWeekRequest = {
  planId: string;
  weekId: string;
};

type AddAthleteWorkoutExerciseRequest = {
  planId: string;
  weekId: string;
  workoutId: string;
  exercise: Pick<Exercise, "name" | "sets" | "reps" | "description">;
};

type PlanTemplatesResponse = {
  data: {
    plans: Plan[];
  };
};

type ExerciseLibraryResponse = {
  data: {
    exercises: Exercise[];
  };
};

type WorkoutLibraryResponse = {
  data: {
    workouts: PlanTemplateWorkout[];
  };
};

type WorkoutLibraryByIdResponse = {
  workout: PlanTemplateWorkout;
};

type SaveToPlanTemplatesLibraryRequest = Pick<Plan, "name" | "planDescription">;

export type UpdatePlanTemplatesLibraryRequest = Pick<
  Plan,
  "planDescription"
> & {
  id: string;
  name?: string;
};

type SaveToWorkoutLibraryRequest = Pick<
  WorkoutFormValues,
  | "name"
  | "workoutCategoryId"
  | "workoutTypeId"
  | "distance"
  | "estimatedTime"
  | "description"
  | "exercises"
>;

export type UpdateWorkoutLibraryRequest = Pick<
  WorkoutFormValues,
  | "workoutCategoryId"
  | "workoutTypeId"
  | "distance"
  | "estimatedTime"
  | "description"
  | "exercises"
> & {
  id: string;
  name?: string;
};
// WorkoutFormValues & {
//   id: string;
// };

export type SaveToExerciseLibraryRequest = Pick<
  Exercise,
  "name" | "description" | "time" | "weight"
> & {
  sets: number;
  reps: number;
};

export type SortPlanTemplateRequest = {
  id: string;
  phases: {
    id: string;
    weeks: {
      id: string;
    }[];
  }[];
};

type CopyFromWorkoutLibraryRequest = {
  planId: string;
  weekId: string;
  date: string;
  workoutLibraryId: string;
};

const trainingSlice = createSlice({
  name: "training",
  initialState: {
    workoutModalOpen: false,
    deleteWorkoutModalOpen: false,
    selectedWorkout: null,
    selectedWeek: null,
    deleteWeekModalOpen: false,
    selectedDate: null,
    selectedDayNumber: null,
  } as TrainingSliceState,
  reducers: {
    setWorkoutModalOpen: (state, action) => {
      state.workoutModalOpen = action.payload;
    },
    setSelectedWorkout: (state, action) => {
      state.selectedWorkout = action.payload;
    },
    setDeleteWorkoutModalOpen: (state, action) => {
      state.deleteWorkoutModalOpen = action.payload;
    },
    setSelectedWeek: (state, action) => {
      state.selectedWeek = action.payload;
    },
    setSelectedDate: (state, action) => {
      state.selectedDate = action.payload;
    },
    setDeleteWeekModalOpen: (state, action) => {
      state.deleteWeekModalOpen = action.payload;
    },
    setSelectedDayNumber: (state, action) => {
      state.selectedDayNumber = action.payload;
    },
  },
});

export const trainingApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getAthleteTrainingPlan: builder.query<AthleteTrainingPlanResponse, string>({
      query: (userId) => {
        const url = `/api/training/plans/athletes/${userId}/plan`;
        return {
          url,
          method: "GET",
          validateStatus: (response, result) => {
            return response.status === 200 || response.status === 404;
          },
        };
      },
      transformResponse: (response: AthleteTrainingPlanResponse, meta) => {
        if (meta?.response?.status === 404) {
          return {
            data: {
              plan: null,
              meta: null,
            },
          };
        }
        return response;
      },
      providesTags: ["Training"],
    }),
    getWorkoutLibrary: builder.query<WorkoutLibraryResponse, void>({
      query: () => {
        const url = `/api/training/workout-library`;
        return {
          url,
          method: "GET",
        };
      },
      providesTags: ["Training"],
    }),
    sortAthleteTrainingPlan: builder.mutation<
      ApiResponse<void>,
      SortAthleteTrainingPlanRequest
    >({
      query: (payload) => {
        const { planId, phases } = payload;
        const url = `/api/training/plans/${planId}/sort`;
        return {
          url,
          method: "PATCH",
          body: { phases },
        };
      },
      invalidatesTags: ["Training"],
    }),
    addAthleteWorkout: builder.mutation<
      ApiResponse<void>,
      AddAthleteWorkoutRequest
    >({
      query: (payload) => {
        const { planId, weekId, workout } = payload;
        const url = `/api/training/plans/${planId}/weeks/${weekId}/workouts`;
        return {
          url,
          method: "POST",
          body: workout,
        };
      },
      invalidatesTags: ["Training"],
    }),
    addAthleteWorkoutExercise: builder.mutation<
      ApiResponse<void>,
      AddAthleteWorkoutExerciseRequest
    >({
      query: (payload) => {
        const { planId, weekId, workoutId, exercise } = payload;
        const url = `/api/training/plans/${planId}/weeks/${weekId}/workouts/${workoutId}/exercises`;
        return {
          url,
          method: "POST",
          body: exercise,
        };
      },
      invalidatesTags: ["Training"],
    }),
    updateAthleteWorkout: builder.mutation<
      ApiResponse<void>,
      UpdateAthleteWorkoutRequest
    >({
      query: (payload) => {
        const { planId, weekId, workoutId, workout } = payload;
        const url = `/api/training/plans/${planId}/weeks/${weekId}/workouts/${workoutId}`;
        return {
          url,
          method: "PATCH",
          body: workout,
        };
      },
      invalidatesTags: ["Training"],
    }),
    deleteAthleteWorkout: builder.mutation<
      ApiResponse<void>,
      DeleteAthleteWorkoutRequest
    >({
      query: (payload) => {
        const { planId, weekId, workoutId } = payload;
        const url = `/api/training/plans/${planId}/weeks/${weekId}/workouts/${workoutId}`;
        return {
          url,
          method: "DELETE",
        };
      },
      invalidatesTags: ["Training"],
    }),
    deleteAthleteWeek: builder.mutation<
      ApiResponse<void>,
      DeleteAthleteWeekRequest
    >({
      query: (payload) => {
        const { planId, weekId } = payload;
        const url = `/api/training/plans/${planId}/weeks/${weekId}`;
        return {
          url,
          method: "DELETE",
        };
      },
      invalidatesTags: ["Training"],
    }),
    saveToWorkoutLibrary: builder.mutation<
      ApiResponse<void>,
      SaveToWorkoutLibraryRequest
    >({
      query: (body) => {
        const url = `/api/training/workout-library`;
        return {
          url,
          method: "POST",
          body,
        };
      },
      invalidatesTags: ["Training", "WorkoutLibrary"],
    }),
    updateWorkoutLibrary: builder.mutation<
      ApiResponse<void>,
      UpdateWorkoutLibraryRequest
    >({
      query: ({ id, ...body }) => {
        const url = `/api/training/workout-library/${id}`;
        return {
          url,
          method: "PATCH",
          body,
        };
      },
      invalidatesTags: ["WorkoutLibrary"],
    }),
    deleteFromWorkoutLibrary: builder.mutation<ApiResponse<void>, string>({
      query: (workoutId) => {
        const url = `/api/training/workout-library/${workoutId}`;
        return {
          url,
          method: "DELETE",
        };
      },
      invalidatesTags: ["WorkoutLibrary"],
    }),
    copyFromWorkoutLibrary: builder.mutation<
      ApiResponse<void>,
      CopyFromWorkoutLibraryRequest
    >({
      query: (body) => {
        const { planId, weekId, date, workoutLibraryId } = body;
        const url = `/api/training/plans/${planId}/weeks/${weekId}/workouts/copy-from-library`;
        return {
          url,
          method: "POST",
          body: { date, workoutLibraryId },
        };
      },
      invalidatesTags: ["Training"],
    }),
    getExerciseLibrary: builder.query<
      ExerciseLibraryResponse,
      { limit: number; offset: number }
    >({
      query: ({ limit, offset }) => {
        const url = `/api/training/exercise-library?limit=${limit}&offset=${offset}`;
        return {
          url,
          method: "GET",
        };
      },
      providesTags: ["ExerciseLibrary"],
    }),
    saveToExerciseLibrary: builder.mutation<
      ApiResponse<void>,
      SaveToExerciseLibraryRequest
    >({
      query: (body) => {
        const url = `/api/training/exercise-library`;
        return {
          url,
          method: "POST",
          body,
        };
      },
      invalidatesTags: ["ExerciseLibrary"],
    }),
    updateExerciseLibrary: builder.mutation<
      ApiResponse<void>,
      SaveToExerciseLibraryRequest & { id: string }
    >({
      query: ({ id, ...body }) => {
        const url = `/api/training/exercise-library/${id}`;
        return {
          url,
          method: "PATCH",
          body,
        };
      },
      invalidatesTags: ["ExerciseLibrary"],
    }),
    deleteExerciseLibrary: builder.mutation<ApiResponse<void>, { id: string }>({
      query: ({ id }) => {
        const url = `/api/training/exercise-library/${id}`;
        return {
          url,
          method: "DELETE",
        };
      },
      invalidatesTags: ["ExerciseLibrary"],
    }),
    getWorkoutsLibrary: builder.query<
      WorkoutLibraryResponse,
      { limit: number; offset: number }
    >({
      query: ({ limit, offset }) => {
        const url = `/api/training/workout-library?limit=${limit}&offset=${offset}`;
        return {
          url,
          method: "GET",
        };
      },
      providesTags: ["WorkoutLibrary"],
    }),
    getWorkoutLibraryById: builder.query<
      ApiResponse<WorkoutLibraryByIdResponse>,
      string
    >({
      query: (id) => {
        const url = `/api/training/workout-library/${id}`;
        return {
          url,
          method: "GET",
        };
      },
      providesTags: ["WorkoutLibrary"],
    }),
    getPlanTemplatesLibrary: builder.query<
      PlanTemplatesResponse,
      { limit: number; offset: number }
    >({
      query: ({ limit, offset }) => {
        const url = `/api/training/plan-templates?limit=${limit}&offset=${offset}`;
        return {
          url,
          method: "GET",
        };
      },
      providesTags: ["PlanTemplatesLibrary"],
    }),
    saveToPlanTemplatesLibrary: builder.mutation<
      ApiResponse<void>,
      SaveToPlanTemplatesLibraryRequest
    >({
      query: (body) => {
        const url = `/api/training/plan-templates`;
        return {
          url,
          method: "POST",
          body,
        };
      },
      invalidatesTags: ["PlanTemplatesLibrary"],
    }),
    updatePlanTemplatesLibrary: builder.mutation<
      ApiResponse<void>,
      UpdatePlanTemplatesLibraryRequest
    >({
      query: ({ id, ...body }) => {
        const url = `/api/training/plan-templates/${id}`;
        return {
          url,
          method: "PATCH",
          body,
        };
      },
      invalidatesTags: ["PlanTemplatesLibrary"],
    }),
    deletePlanTemplatesLibrary: builder.mutation<ApiResponse<void>, string>({
      query: (id) => {
        const url = `/api/training/plan-templates/${id}`;
        return {
          url,
          method: "DELETE",
        };
      },
      invalidatesTags: ["PlanTemplatesLibrary"],
    }),
    getPlanTemplate: builder.query<ApiResponse<{ plan: PlanTemplate }>, string>(
      {
        query: (id) => {
          const url = `/api/training/plan-templates/${id}`;
          return {
            url,
            method: "GET",
          };
        },
        providesTags: ["PlanTemplatesLibrary"],
      },
    ),
    sortPlanTemplate: builder.mutation<
      ApiResponse<void>,
      SortPlanTemplateRequest
    >({
      query: ({ id, ...body }) => {
        const url = `/api/training/plan-templates/${id}/sort`;
        return {
          url,
          method: "PATCH",
          body,
        };
      },
      invalidatesTags: ["PlanTemplatesLibrary"],
    }),
    createPlanTemplateWeek: builder.mutation<
      ApiResponse<void>,
      { planTemplateId: string; phaseId: string }
    >({
      query: ({ planTemplateId, phaseId }) => {
        const url = `/api/training/plan-templates/${planTemplateId}/phases/${phaseId}/weeks`;
        return {
          url,
          method: "POST",
          body: {
            description: "",
          },
        };
      },
      invalidatesTags: ["PlanTemplatesLibrary"],
    }),
    updatePlanTemplateWeek: builder.mutation<
      ApiResponse<void>,
      {
        planTemplateId: string;
        phaseId: string;
        weekId: string;
        description: string;
      }
    >({
      query: ({ planTemplateId, phaseId, weekId, description }) => {
        const url = `/api/training/plan-templates/${planTemplateId}/phases/${phaseId}/weeks/${weekId}`;
        return {
          url,
          method: "PATCH",
          body: { description },
        };
      },
      invalidatesTags: ["PlanTemplatesLibrary"],
    }),
    createPlanTemplatePhase: builder.mutation<
      ApiResponse<{ phase: PlanPhase }>,
      string
    >({
      query: (planTemplateId) => {
        const url = `/api/training/plan-templates/${planTemplateId}/phases`;
        return {
          url,
          method: "POST",
          body: {
            name: "Phase 1",
            description: "",
          },
        };
      },
      invalidatesTags: ["PlanTemplatesLibrary"],
    }),
    deletePlanTemplateWeek: builder.mutation<
      ApiResponse<void>,
      { planTemplateId: string; phaseId: string; weekId: string }
    >({
      query: ({ planTemplateId, phaseId, weekId }) => {
        const url = `/api/training/plan-templates/${planTemplateId}/phases/${phaseId}/weeks/${weekId}`;
        return {
          url,
          method: "DELETE",
        };
      },
      invalidatesTags: ["PlanTemplatesLibrary"],
    }),
    addPlanTemplateWeekWorkout: builder.mutation<
      ApiResponse<void>,
      {
        planTemplateId: string;
        phaseId: string;
        weekId: string;
        workout: Partial<WorkoutFormValues>;
        dayNumber: number;
      }
    >({
      query: ({ planTemplateId, phaseId, weekId, workout, dayNumber }) => {
        const url = `/api/training/plan-templates/${planTemplateId}/phases/${phaseId}/weeks/${weekId}/workouts`;
        return {
          url,
          method: "POST",
          body: {
            ...workout,
            dayNumber,
          },
        };
      },
      invalidatesTags: ["PlanTemplatesLibrary"],
    }),
    updatePlanTemplateWeekWorkout: builder.mutation<
      ApiResponse<void>,
      {
        planTemplateId: string;
        phaseId: string;
        weekId: string;
        workoutId: string;
        workout: Partial<WorkoutFormValues>;
        dayNumber: number;
      }
    >({
      query: ({
        planTemplateId,
        phaseId,
        weekId,
        workoutId,
        workout,
        dayNumber,
      }) => {
        const url = `/api/training/plan-templates/${planTemplateId}/phases/${phaseId}/weeks/${weekId}/workouts/${workoutId}`;
        return {
          url,
          method: "PATCH",
          body: {
            ...workout,
            dayNumber,
          },
        };
      },
      invalidatesTags: ["PlanTemplatesLibrary"],
    }),
    deletePlanTemplateWeekWorkout: builder.mutation<
      ApiResponse<void>,
      {
        planTemplateId: string;
        phaseId: string;
        weekId: string;
        workoutId: string;
      }
    >({
      query: ({ planTemplateId, phaseId, weekId, workoutId }) => {
        const url = `/api/training/plan-templates/${planTemplateId}/phases/${phaseId}/weeks/${weekId}/workouts/${workoutId}`;
        return {
          url,
          method: "DELETE",
        };
      },
      invalidatesTags: ["PlanTemplatesLibrary"],
    }),
    copyPlanTemplateWeekWorkout: builder.mutation<
      ApiResponse<void>,
      {
        planTemplateId: string;
        phaseId: string;
        weekId: string;
        workoutLibraryId: string;
        dayNumber: number;
      }
    >({
      query: ({
        planTemplateId,
        phaseId,
        weekId,
        workoutLibraryId,
        dayNumber,
      }) => {
        const url = `/api/training/plan-templates/${planTemplateId}/phases/${phaseId}/weeks/${weekId}/workouts/copy-from-library`;
        return {
          url,
          method: "POST",
          body: { workoutLibraryId, dayNumber },
        };
      },
      invalidatesTags: ["PlanTemplatesLibrary"],
    }),
    copyPlanTemplateWeek: builder.mutation<
      ApiResponse<void>,
      {
        planId: string;
        phaseId: string;
        planTemplateId: string;
        weekId: string;
      }
    >({
      query: ({ planId, phaseId, planTemplateId, weekId }) => {
        const url = `/api/training/plan-templates/${planId}/phases/${phaseId}/weeks/copy-from-template`;
        return {
          url,
          method: "POST",
          body: {
            planTemplateId,
            weekId,
          },
        };
      },
      invalidatesTags: ["PlanTemplatesLibrary"],
    }),
    assignTrainingPlan: builder.mutation<
      ApiResponse<void>,
      {
        athleteId: string;
        planTemplateId: string;
        date?: string;
      }
    >({
      query: ({ athleteId, planTemplateId, date }) => {
        const url = `/api/training/plans/assign`;
        return {
          url,
          method: "POST",
          body: {
            athleteId,
            planTemplateId,
            date,
          },
        };
      },
      invalidatesTags: ["PlanTemplatesLibrary", "Users", "Training"],
    }),
    endTrainingPlan: builder.mutation<
      ApiResponse<void>,
      {
        planId: string;
      }
    >({
      query: ({ planId }) => {
        const url = `/api/training/plans/${planId}/endplan`;
        return {
          url,
          method: "PATCH",
          body: {
            planId,
          },
        };
      },
      invalidatesTags: ["PlanTemplatesLibrary", "Users", "Training"],
    }),
    startAssignedTrainingPlan: builder.mutation<
      ApiResponse<void>,
      {
        planId: string;
      }
    >({
      query: ({ planId }) => {
        const url = `/api/training/plans/${planId}/startplan`;
        return {
          url,
          method: "PATCH",
          body: {
            planId,
          },
        };
      },
      invalidatesTags: ["PlanTemplatesLibrary", "Users", "Training"],
    }),
    getMessages: builder.query<ApiResponse<{ messages: Message[] }>, void>({
      query: () => {
        const url = `/api/training/messages`;
        return {
          url,
          method: "GET",
        };
      },
      providesTags: ["MessagesWithUser"],
    }),
    getChats: builder.query<ApiResponse<{ chats: Chat[] }>, void>({
      query: () => {
        const url = `/api/training/chats`;
        return {
          url,
          method: "GET",
        };
      },
      providesTags: ["Chats"],
    }),
    getGroupChats: builder.query<
      ApiResponse<{ groupChats: GroupChat[]; total: number }>,
      void
    >({
      query: () => {
        const url = `/api/training/group-chats`;
        return {
          url,
          method: "GET",
        };
      },
      providesTags: ["Chats"],
    }),
    getChatsWithAthlete: builder.query<
      ApiResponse<{ messages: Message[] }>,
      string
    >({
      query: (athleteId) => {
        const url = `/api/training/chats/${athleteId}/messages`;
        return {
          url,
          method: "GET",
        };
      },
      providesTags: ["Chats"],
    }),
    getGroupChatsMessages: builder.query<
      ApiResponse<{ messages: Message[]; total: number }>,
      string
    >({
      query: (groupChatId) => {
        const url = `/api/training/group-chats/${groupChatId}/messages`;
        return {
          url,
          method: "GET",
        };
      },
      providesTags: ["Chats"],
    }),
    sendChatMessage: builder.mutation<
      ApiResponse<Message>,
      { athleteId: string; message: string }
    >({
      query: ({ athleteId, message }) => {
        const url = `/api/training/chats/${athleteId}/messages`;
        return {
          url,
          method: "POST",
          body: { message },
        };
      },
      invalidatesTags: ["Chats"],
    }),
    sendGroupChatMessage: builder.mutation<
      ApiResponse<Message>,
      { groupChatId: string; message: string }
    >({
      query: ({ groupChatId, message }) => {
        const url = `/api/training/group-chats/${groupChatId}/messages`;
        return {
          url,
          method: "POST",
          body: { message, groupChatId },
        };
      },
      invalidatesTags: ["Chats"],
    }),
    markChatAsRead: builder.mutation<ApiResponse<void>, string>({
      query: (athleteId) => {
        const url = `/api/training/chats/${athleteId}/read`;
        return {
          url,
          method: "PATCH",
        };
      },
      invalidatesTags: ["Chats", "Profile"],
    }),
    createGroupChat: builder.mutation<ApiResponse<GroupChat>, string>({
      query: (name) => {
        const url = `/api/training/group-chats`;
        return {
          url,
          method: "POST",
          body: { name },
        };
      },
      invalidatesTags: ["Chats", "Profile"],
    }),
    deleteGroupChat: builder.mutation<ApiResponse<void>, string>({
      query: (id) => {
        const url = `/api/training/group-chats/${id}`;
        return {
          url,
          method: "DELETE",
        };
      },
      invalidatesTags: ["Chats", "Profile"],
    }),
    joinGroupChat: builder.mutation<ApiResponse<void>, string>({
      query: (groupChatId) => {
        const url = `/api/training/group-chats/${groupChatId}/join`;
        return {
          url,
          method: "POST",
        };
      },
      invalidatesTags: ["Chats", "Profile"],
    }),
    leaveGroupChat: builder.mutation<ApiResponse<void>, string>({
      query: (groupChatId) => {
        const url = `/api/training/group-chats/${groupChatId}/leave`;
        return {
          url,
          method: "DELETE",
        };
      },
      invalidatesTags: ["Chats", "Profile"],
    }),
  }),
});

export const selectWorkoutModalOpen = (state: RootState) =>
  state.training.workoutModalOpen;

export const selectDeleteWorkoutModalOpen = (state: RootState) =>
  state.training.deleteWorkoutModalOpen;

export const selectSelectedWorkout = (state: RootState) =>
  state.training.selectedWorkout;

export const selectSelectedWeek = (state: RootState) =>
  state.training.selectedWeek;

export const selectSelectedDate = (state: RootState) =>
  state.training.selectedDate;

export const selectDeleteWeekModalOpen = (state: RootState) =>
  state.training.deleteWeekModalOpen;

export const selectSelectedDayNumber = (state: RootState) =>
  state.training.selectedDayNumber;

export const {
  useGetAthleteTrainingPlanQuery,
  useLazyGetAthleteTrainingPlanQuery,
  useGetWorkoutLibraryQuery,
  useSortAthleteTrainingPlanMutation,
  useUpdateAthleteWorkoutMutation,
  useDeleteAthleteWorkoutMutation,
  useAddAthleteWorkoutExerciseMutation,
  useAddAthleteWorkoutMutation,
  useSaveToWorkoutLibraryMutation,
  useCopyFromWorkoutLibraryMutation,
  useDeleteAthleteWeekMutation,
  useGetPlanTemplatesLibraryQuery,
  useGetExerciseLibraryQuery,
  useSaveToExerciseLibraryMutation,
  useUpdateExerciseLibraryMutation,
  useDeleteExerciseLibraryMutation,
  useGetWorkoutsLibraryQuery,
  useDeleteFromWorkoutLibraryMutation,
  useUpdateWorkoutLibraryMutation,
  useSaveToPlanTemplatesLibraryMutation,
  useUpdatePlanTemplatesLibraryMutation,
  useDeletePlanTemplatesLibraryMutation,
  useGetPlanTemplateQuery,
  useSortPlanTemplateMutation,
  useCreatePlanTemplateWeekMutation,
  useUpdatePlanTemplateWeekMutation,
  useCreatePlanTemplatePhaseMutation,
  useDeletePlanTemplateWeekMutation,
  useCopyPlanTemplateWeekWorkoutMutation,
  useAddPlanTemplateWeekWorkoutMutation,
  useUpdatePlanTemplateWeekWorkoutMutation,
  useGetWorkoutLibraryByIdQuery,
  useLazyGetWorkoutLibraryByIdQuery,
  useCopyPlanTemplateWeekMutation,
  useLazyGetPlanTemplatesLibraryQuery,
  useLazyGetPlanTemplateQuery,
  useDeletePlanTemplateWeekWorkoutMutation,
  useAssignTrainingPlanMutation,
  useEndTrainingPlanMutation,
  useStartAssignedTrainingPlanMutation,
  useGetMessagesQuery,
  useGetChatsWithAthleteQuery,
  useLazyGetChatsWithAthleteQuery,
  useSendChatMessageMutation,
  useGetChatsQuery,
  useLazyGetChatsQuery,
  useGetGroupChatsQuery,
  useLazyGetGroupChatsQuery,
  useMarkChatAsReadMutation,
  useCreateGroupChatMutation,
  useDeleteGroupChatMutation,
  useGetGroupChatsMessagesQuery,
  useLazyGetGroupChatsMessagesQuery,
  useSendGroupChatMessageMutation,
  useJoinGroupChatMutation,
  useLeaveGroupChatMutation,
} = trainingApiSlice;

export const {
  setWorkoutModalOpen,
  setDeleteWorkoutModalOpen,
  setSelectedWorkout,
  setSelectedWeek,
  setSelectedDate,
  setDeleteWeekModalOpen,
  setSelectedDayNumber,
} = trainingSlice.actions;
export default trainingSlice.reducer;
